/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { renditionsApi } from "../AlfrescoApi.js";

/**
 * Service deciding whether a node can be previewed in the browser.
 *
 * Some file types are rendered natively (images, videos, PDF). Every other
 * type relies on a server-side "pdf" rendition produced by Alfresco's
 * transform service. Asking Alfresco to create that rendition for a type it
 * cannot transform (archives, binaries, ...) generates noise in the logs and
 * dead-end viewers.
 *
 * `listRenditions` (GET /nodes/{id}/renditions) returns only the renditions
 * applicable to the node's source mimetype and does NOT trigger any transform,
 * so it is a cheap, side-effect-free way to know up-front whether a "pdf"
 * rendition is available. Results are cached per mimetype for the session and
 * are reusable across apps (espaces, marches, media, ...).
 */
class PreviewService {
  constructor() {
    // mimeType -> boolean (a "pdf" rendition is available for this type)
    this.previewableByMimeType = new Map();
  }

  /**
   * Types rendered directly by the front-end, without a server-side rendition.
   * @param {string} mimeType
   * @returns {boolean}
   */
  isNativelyPreviewable(mimeType) {
    if (!mimeType) {
      return false;
    }
    return (
      mimeType.startsWith("image/") ||
      mimeType.startsWith("video/") ||
      mimeType === "application/pdf"
    );
  }

  /**
   * Tell whether the given node can be previewed (natively or through a "pdf"
   * rendition). Never throws: on error it returns false so callers can fall
   * back to a download-only view.
   *
   * @param {object} node a node with `id` and `content.mimeType`
   * @returns {Promise<boolean>}
   */
  async isPreviewable(node) {
    const mimeType = node?.content?.mimeType;
    if (!mimeType) {
      return false;
    }
    if (this.isNativelyPreviewable(mimeType)) {
      return true;
    }
    if (this.previewableByMimeType.has(mimeType)) {
      return this.previewableByMimeType.get(mimeType);
    }

    try {
      const paging = await renditionsApi.listRenditions(node.id);
      const entries = paging?.list?.entries ?? [];
      const hasPdf = entries.some((e) => e.entry?.id === "pdf");
      // Cache only successful answers, keyed by mimetype.
      this.previewableByMimeType.set(mimeType, hasPdf);
      return hasPdf;
    } catch (error) {
      // Transient/permission errors must not poison the cache.
      console.debug(
        `isPreviewable: listRenditions failed for ${node.id}: ${error?.message}`,
      );
      return false;
    }
  }
}

export default new PreviewService();
